import React from 'react';
import { Box, Popover, SpaceBetween } from '@cloudscape-design/components';
import './IlmpIssueDetails.css';

// Issue fields arrive as arrays, JSON-encoded arrays, or individual messages.
const normalizeMessages = value => {
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return normalizeMessages(parsed);
    } catch (_) {}
    return value.trim() ? [value] : [];
  }
  if (!Array.isArray(value)) return [];
  return Array.from(new Set(value.filter(message => typeof message === 'string' && message.trim())));
};

// Preserve legacy messages while separating the markers added by the issue formatter.
const BlockingIssue = ({ message }) => {
  const match = message.match(/^([\s\S]*?)(?:\s+ESDC rule:\s*([\s\S]*?))?(?:\s+Fix:\s*([\s\S]*))?$/);
  const [, blocker, rule, fix] = match;
  return (
    <SpaceBetween size="xxs">
      <div><strong>Blocker:</strong> {blocker}</div>
      {fix && <div><strong>Fix:</strong> {fix}</div>}
      {rule && <Box color="text-body-secondary" fontSize="body-s">ESDC rule: {rule}</Box>}
    </SpaceBetween>
  );
};

const IlmpIssueDetails = ({
  blockingIssues,
  warnings,
  messages,
  subject,
  summary,
  header = 'ILMP validation details',
  empty = '—',
  renderWithPortal = true,
}) => {
  const groups = [
    { label: 'Blocking issues', items: normalizeMessages(blockingIssues) },
    { label: 'Warnings', items: normalizeMessages(warnings) },
    { label: 'Details', items: normalizeMessages(messages) },
  ].filter(group => group.items.length);
  const count = groups.reduce((total, group) => total + group.items.length, 0);
  if (!count) return empty;

  const label = summary || `${count} issue${count === 1 ? '' : 's'}: ${groups[0].items[0]}`;
  return (
    <Popover
      className="ilmp-issue-details"
      header={header}
      size="large"
      position="right"
      triggerType="text"
      triggerAriaLabel={`View ${header.toLowerCase()}${subject ? ` for ${subject}` : ''}: ${count} item${count === 1 ? '' : 's'}`}
      wrapTriggerText={false}
      dismissAriaLabel="Close ILMP details"
      renderWithPortal={renderWithPortal}
      content={(
        <div className="ilmp-issue-details__content" role="region" aria-label={header} tabIndex={0}>
          <SpaceBetween size="s">
            {subject && <Box variant="strong">{subject}</Box>}
            {groups.map(group => (
              <div key={group.label}>
                <Box variant="strong">{group.label} ({group.items.length})</Box>
                <ul className="ilmp-issue-details__list">
                  {group.items.map((message, index) => <li key={index}>
                    {group.label === 'Blocking issues' ? <BlockingIssue message={message} /> : message}
                  </li>)}
                </ul>
              </div>
            ))}
          </SpaceBetween>
        </div>
      )}
    >
      {label}
    </Popover>
  );
};

export default IlmpIssueDetails;
